import { useState } from 'react';

import { ApiError } from '../../api/problem';

/**
 * One submit handler for every form in the app.
 *
 * It exists to make `13 §6`'s rule — *"422 mapeado a los campos del
 * formulario"* — the path of least resistance: a 422's `errors[]` lands in
 * `fields` keyed by the input name, and anything else lands in `error` for the
 * banner. A screen that forgot to split them would show "Validation failed" and
 * leave the user hunting for which box is wrong.
 */
export function useSubmit<T>() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const submit = async (run: () => Promise<T>, onDone?: (result: T) => void): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    setFields({});

    try {
      const result = await run();
      onDone?.(result);

      return result;
    } catch (caught) {
      // A 422 goes to the fields **only if it names any**. Not every 422 is
      // about an input: `POST /github/install-url` refuses with one when the
      // deployment has no App registered, and `PATCH /settings` can refuse the
      // document as a whole. Routing those into `fields` put them on no input
      // and no banner, so the button did nothing and said nothing — the one
      // failure mode worse than a bad message.
      const violations = caught instanceof ApiError && caught.status === 422 ? caught.fields : {};

      if (Object.keys(violations).length > 0) {
        setFields(violations);
      } else {
        setError(caught);
      }

      return undefined;
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, fields, submit, reset: () => setError(null) };
}
